import React, { useState, useRef, useMemo } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { nid, nowISO } from '../../utils/helpers';
import EngineResponse from './EngineResponse';
import { EmptyState } from '../common/EmptyState';
import { knowledgeLibraryServices } from '../../APIServices/knowledgeLibraryServices';

function SearchModule({ onUseInReport }) {
  const { state, audit } = useData();
  const toast = useToast();
  const [conversation, setConversation] = useState([]);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState({ molecule: 'all', region: 'all' });
  const inputRef = useRef();

  const knowledge = state.knowledge.filter((k) => !k.deleted && k.status === 'approved');

  const scopedKnowledge = useMemo(() => {
    return knowledge.filter((k) => {
      if (scope.molecule !== 'all' && (k.molecule || '').toLowerCase() !== scope.molecule.toLowerCase()) return false;
      if (scope.region !== 'all' && (k.region || '').toLowerCase() !== scope.region.toLowerCase() && (k.region || '').toLowerCase() !== 'global') return false;
      return true;
    });
  }, [knowledge, scope]);

  const handleAsk = async (q = query) => {
    if (!q.trim()) return;

    const userMsg = { id: nid('m'), role: 'user', content: q, at: nowISO() };
    let data;
    try {
      data = await knowledgeLibraryServices.searchKnowledgeLibrary({
        query: q,
        scope,
        limit: 5,
      });
    } catch (error) {
      toast('Search failed. Please try again.', 'error');
      return;
    }

    const result = data?.data || {
      query: q,
      entities: { molecules: [], parameters: [], regions: [], studyTypes: [], all: [] },
      answer: {
        type: 'missing',
        heading: 'Insufficient evidence',
        body: 'No approved sources available for this query.',
        missing: ['Upload approved knowledge sources and try again'],
      },
      sources: [],
      snippets: [],
      conflicts: [],
      totalScored: 0,
    };

    const engineMsg = {
      id: nid('m'),
      role: 'engine',
      at: nowISO(),
      query: q,
      result,
      engineVersion: 'v2.3.1',
      rulePack: 'v14',
      templateVersion: 'v8'
    };

    setConversation((c) => [...c, userMsg, engineMsg]);
    audit('KNOWLEDGE_SEARCH', `Query: "${q.slice(0, 60)}"`, `${result.sources.length} sources cited`, { searchQuery: q });
    setQuery('');
  };

  const handleNewSession = () => {
    setConversation([]);
    inputRef.current?.focus();
  };

  const suggestions = [
    'What CV% should we use for apixaban Cmax in a BABE sample size calc?',
    'Is apixaban classified as an NTI or HVD?',
    'What sample size does CDSCO require for a 2x2 BABE with CV ~25%?',
    'How has BioServe performed on past Arcolab BABE projects?',
    'What is the half-life of dapagliflozin?'
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Section 15.4 · ChatGPT-like, deterministic</div>
          <h1 className="page-title">Knowledge search</h1>
          <p className="page-sub">Ask in plain language. Answers grounded only in approved sources with full citation trail. No external API calls.</p>
        </div>
        {conversation.length > 0 && (
          <button className="btn" onClick={handleNewSession}>
            <i className="ti ti-refresh" /> New session
          </button>
        )}
      </div>

      <div className="card card-pad mb-4" style={{ background: 'linear-gradient(180deg, var(--surface-2), var(--surface))' }}>
        <div className="flex gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
          <select className="btn btn-sm" value={scope.molecule} onChange={(e) => setScope({ ...scope, molecule: e.target.value })}>
            <option value="all">All molecules</option>
            {[...new Set(knowledge.map((k) => k.molecule).filter(Boolean))].map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <select className="btn btn-sm" value={scope.region} onChange={(e) => setScope({ ...scope, region: e.target.value })}>
            <option value="all">All regions</option>
            <option value="India">India / CDSCO</option>
            <option value="USA">USA / FDA</option>
            <option value="EU">EU / EMA</option>
          </select>
          <div className="xs muted" style={{ marginLeft: 'auto', alignSelf: 'center' }}>
            <span className="mono">Engine v2.3.1</span> · {scopedKnowledge.length} approved sources in scope
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <textarea
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAsk();
            }}
            placeholder="Ask the knowledge base..."
            style={{
              width: '100%',
              minHeight: 72,
              padding: '14px 100px 14px 16px',
              border: '1px solid var(--line-2)',
              borderRadius: 2,
              fontSize: 14,
              fontFamily: 'var(--sans)',
              resize: 'vertical',
              background: 'white',
              outline: 'none'
            }}
          />
          <button
            className="btn btn-primary"
            onClick={() => handleAsk()}
            disabled={!query.trim()}
            style={{ position: 'absolute', right: 10, bottom: 10 }}
          >
            <i className="ti ti-arrow-up" /> Ask
          </button>
        </div>

        {conversation.length === 0 && (
          <div className="flex gap-2 mt-3" style={{ flexWrap: 'wrap' }}>
            <span className="xs muted" style={{ alignSelf: 'center' }}>Try:</span>
            {suggestions.map((s, i) => (
              <button key={i} className="btn btn-xs" onClick={() => handleAsk(s)} style={{ fontFamily: 'var(--display)', fontStyle: 'italic', fontWeight: 400 }}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {conversation.length === 0 ? (
        <EmptyState
          icon="ti-message-chatbot"
          title="Start a search session"
          desc="Your questions and the engine's source-cited answers will appear here."
        />
      ) : (
        <div>
          {conversation.map((msg) => {
            if (msg.role === 'user') {
              return (
                <div className="msg" key={msg.id}>
                  <div className="msg-avatar user">{state.users.find((u) => u.id === state.currentUserId)?.initials || 'U'}</div>
                  <div className="msg-body">
                    <div className="msg-meta">You · {new Date(msg.at).toLocaleTimeString()}</div>
                    <div className="msg-content user">{msg.content}</div>
                  </div>
                </div>
              );
            }
            return <EngineResponse key={msg.id} msg={msg} onUseInReport={onUseInReport} />;
          })}
        </div>
      )}
    </div>
  );
}

export default SearchModule;